// Geteilte Cluster-Logik für die To-Do-/Vorgangs-Liste (D17).
//
// Kapselt das Pill-Modell (D1/D4), damit die Dashboard-Karte „Meine Arbeit"
// UND später die volle Vorgänge-Seite dieselbe Logik nutzen (keine
// Duplizierung). Cluster sind NICHT-disjunkt: eine Position darf in mehreren
// Pills erscheinen (reine Filtersicht).
//
//   Heute          — Frist ODER TWV = heute (überfällige Frist rot, isOverdueFrist)
//   Woche          — Frist ODER TWV in dieser KW (Mo–So)
//   Info           — zweiseitig (Info erwartet / Info geben), aus zuInformierende
//   Alle offen     — status ≠ Erledigt (auch ohne Datum, inkl. Info, inkl. Entwürfe)
//   Alle erledigt  — status = Erledigt
//
// „Heute" und „Woche" fassen seit PO 07.09.2026 zusammen, was vorher vier
// Pills waren („heute und Woche statt heute geplant und heute fällig und
// Woche geplant und Woche fällig"). Die Trennung fällig/geplant (Frist/TWV)
// beantwortete eine Frage, die beim Überblick niemand stellt — wer heute etwas
// vorhat, will es an EINER Stelle sehen; welches der beiden Daten es dorthin
// gebracht hat, steht in der Zeile selbst.
import { useMemo } from "react";
import type { TodoDto } from "../../services/vorgangService";
import { isInfoEmpfaenger, isInfoItem, type ScopeCtx } from "../../utils/vorgangScope";
import { isDueToday, isPlannedToday, isThisWeekDue, isThisWeekPlanned } from "./DashboardShared";

/** Die 5 Cluster-Schlüssel in fester Reihenfolge. */
export type ClusterKey = "heute" | "woche" | "info" | "offen" | "erledigt";

/** Minimal-Form, die ein Listen-Item für die Cluster-Auswertung erfüllen muss. */
export interface ClusterItem {
    status: string;
    frist?: string | null;
    twv?: string | null;
    /**
     * Zugrunde liegendes To-Do — nur für die Info-Auswertung (zuInformierende +
     * Rolle). Vorgangs-Items lassen das Feld weg → nie im Info-Cluster.
     */
    todo?: TodoDto;
}

export interface ClusterDef {
    key: ClusterKey;
    label: string;
}

/** Feste Pill-Reihenfolge & deutsche Labels (D1/D4). */
export const TODO_CLUSTERS: ClusterDef[] = [
    { key: "heute", label: "Heute" },
    { key: "woche", label: "Woche" },
    { key: "info", label: "Info" },
    { key: "offen", label: "Alle offen" },
    { key: "erledigt", label: "Alle erledigt" },
];

/** Default-Pill: womit der Tag anfängt. */
export const DEFAULT_CLUSTER: ClusterKey = "heute";

const isOffen = (i: ClusterItem): boolean => i.status !== "Erledigt";

/**
 * Prädikat je Cluster — nicht-disjunkt (eine Position kann mehrfach matchen).
 * Exportiert, damit die Dashboard-Karte „Meine Arbeit" die Cluster-Zugehörigkeit
 * eines Vorgangs aus SEINEN offenen To-dos ODER seinen eigenen Daten ableiten
 * kann (Vorgangs-TWV zuerst), ohne die Prädikate zu duplizieren.
 */
export function matchesCluster(key: ClusterKey, i: ClusterItem, ctx: ScopeCtx): boolean {
    switch (key) {
        // Frist ODER TWV — beide Daten führen in denselben Topf. Ein Vorgang
        // mit Frist heute und TWV morgen steht einmal unter „Heute", nicht in
        // zwei nebeneinanderliegenden Pills mit derselben Zeile.
        case "heute":
            return isOffen(i) && (isDueToday(i.frist) || isPlannedToday(i.twv));
        case "woche":
            return isOffen(i) && (isThisWeekDue(i.frist) || isThisWeekPlanned(i.twv));
        case "info":
            // Info nur aus zuInformierende + Rolle (D6); Vorgangs-Items (kein todo) nie.
            return !!i.todo && i.status !== "Erledigt" && isInfoItem(i.todo, ctx);
        case "offen":
            // Gesamtsicht: alles Unerledigte (auch ohne Datum, inkl. Info).
            return i.status !== "Erledigt";
        case "erledigt":
            return i.status === "Erledigt";
        default:
            return false;
    }
}

const sortKey = (i: ClusterItem): number => {
    const d = i.frist || i.twv;
    return d ? new Date(d).getTime() : Number.MAX_SAFE_INTEGER;
};

export interface UseTodoClustersResult<T extends ClusterItem> {
    /** Pill-Definitionen in fester Reihenfolge (für die Filter-Leiste). */
    pills: ClusterDef[];
    /** Anzahl der Items pro Cluster (Filtersicht, nicht-disjunkt). */
    countFor: (key: ClusterKey) => number;
    /** Gefilterte + nach Frist/TWV sortierte Items eines Clusters. */
    itemsFor: (key: ClusterKey) => T[];
    /** Info-Items aufgeteilt in die zwei beschrifteten Gruppen (D6). */
    infoGroups: { erwartet: T[]; geben: T[] };
}

/**
 * Geteilte Cluster-Logik (D17). Liefert pro Cluster die gefilterten Items +
 * Counts. `T` darf ein erweitertes Item sein (z. B. UI-WorkItem), solange es
 * `ClusterItem` erfüllt.
 */
export function useTodoClusters<T extends ClusterItem>(
    items: T[],
    scopeCtx: ScopeCtx,
): UseTodoClustersResult<T> {
    const counts = useMemo(() => {
        const c = {} as Record<ClusterKey, number>;
        for (const def of TODO_CLUSTERS) c[def.key] = 0;
        for (const it of items)
            for (const def of TODO_CLUSTERS) if (matchesCluster(def.key, it, scopeCtx)) c[def.key] += 1;
        return c;
    }, [items, scopeCtx]);

    const byCluster = useMemo(() => {
        const m = {} as Record<ClusterKey, T[]>;
        for (const def of TODO_CLUSTERS) {
            m[def.key] = items
                .filter((it) => matchesCluster(def.key, it, scopeCtx))
                .sort((a, b) => sortKey(a) - sortKey(b));
        }
        return m;
    }, [items, scopeCtx]);

    const infoGroups = useMemo(() => {
        const erwartet: T[] = [];
        const geben: T[] = [];
        for (const it of byCluster.info) {
            if (!it.todo) continue;
            // „Info erwartet" hat Vorrang in der Darstellung, falls beides zuträfe.
            if (isInfoEmpfaenger(it.todo, scopeCtx)) erwartet.push(it);
            else geben.push(it);
        }
        return { erwartet, geben };
    }, [byCluster.info, scopeCtx]);

    return {
        pills: TODO_CLUSTERS,
        countFor: (key) => counts[key] ?? 0,
        itemsFor: (key) => byCluster[key] ?? [],
        infoGroups,
    };
}
